const eventing = require('../helpers/eventing');
const defineProperties = require('../helpers/defineProperties');
const hasPassiveCapability = require('./hasPassiveCapability.js');
const uuid = require('uuid');

const defaultLogging = require('../ot/logging.js');
const DefaultOTHelpers = require('../common-js-helpers/OTHelpers');
const DefaultOTPlugin = require('../otplugin/otplugin.js');
const defaultIsiOS = require('./isiOS');
const defaultiOSVersion = require('./iOSVersion');
const defaultVersionGreaterThan = require('../otplugin/version_greater_than');
const defaultEnv = require('../helpers/env');

const DefaultVideoElementFacadeFactory = require('./video_element/index.js');

/**
 * WidgetViewFactory - DI factory for WidgetView
 *
 * @package
 * @param {Object} deps
 * @param {VideoElementFacade} deps.VideoElementFacade
 * @return WidgetView
 */
function WidgetViewFactory({
  logging = defaultLogging,
  OTHelpers = DefaultOTHelpers,
  OTPlugin = DefaultOTPlugin,
  isiOS = defaultIsiOS,
  iOSVersion = defaultiOSVersion,
  versionGreaterThan = defaultVersionGreaterThan,
  VideoElementFacade = DefaultVideoElementFacadeFactory(),
  env = defaultEnv,
} = {}) {
  const miniWidth = 128;
  const miniHeight = 128;
  const microWidth = 64;
  const microHeight = 64;

  // We pass in the real window rather than a mock because hasPassiveCapability
  // needs to attach/remove real event listeners on the window which won't work
  // using the mock window.
  const supportsPassive = hasPassiveCapability({ window: global });

  function fixMini(container, width, height) {
    const w = parseInt(width, 10);
    const h = parseInt(height, 10);

    if (w < microWidth || h < microHeight) {
      OTHelpers.addClass(container, 'OT_micro');
    } else {
      OTHelpers.removeClass(container, 'OT_micro');
    }
    if (w < miniWidth || h < miniHeight) {
      OTHelpers.addClass(container, 'OT_mini');
    } else {
      OTHelpers.removeClass(container, 'OT_mini');
    }
  }

  const getOrCreateContainer = function getOrCreateContainer(elementOrDomId, insertMode) {
    let container;
    let domId;

    if (elementOrDomId && elementOrDomId.nodeName) {
      // It looks like we were given a DOM element. Grab the id or generate
      // one if it doesn't have one.
      container = elementOrDomId;
      if (!container.getAttribute('id') || container.getAttribute('id').length === 0) {
        container.setAttribute('id', `OT_${uuid()}`);
      }

      domId = container.getAttribute('id');
    } else if (elementOrDomId) {
      // We may have got an id, try and get it's DOM element.
      container = OTHelpers(`#${elementOrDomId}`).get(0);
      if (container) { domId = elementOrDomId; }
    }

    if (!domId) {
      domId = `OT_${uuid().replace(/-/g, '_')}`;
    }

    if (!container) {
      container = OTHelpers.createElement('div', { id: domId });
      container.style.backgroundColor = '#000000';
      document.body.appendChild(container);
    } else if (!(insertMode == null || insertMode === 'replace')) {
      const placeholder = document.createElement('div');
      placeholder.id = (`OT_${uuid()}`);
      if (insertMode === 'append') {
        container.appendChild(placeholder);
        container = placeholder;
      } else if (insertMode === 'before') {
        container.parentNode.insertBefore(placeholder, container);
        container = placeholder;
      } else if (insertMode === 'after') {
        container.parentNode.insertBefore(placeholder, container.nextSibling);
        container = placeholder;
      }
    } else {
      OTHelpers.emptyElement(container);
    }

    return container;
  };

  /**
   * WidgetView - A standard abstraction used by Subscriber and Publisher for
   * displaying the video and it's UI.
   *
   * @package
   * @class
   * @param {Element} targetElement the element to attach this WidgetView to
   * @param {Object} properties
   */
  function WidgetView(targetElement, properties) {
    const oldContainerStyles = {};
    let loading = true;
    let audioOnly = false;
    let fitMode = 'cover';
    let sizeObserver;
    let videoElementFacade;
    let videoObserver;
    let width;
    let height;
    let container;

    if (properties.insertDefaultUI !== false) {
      container = getOrCreateContainer(targetElement, properties && properties.insertMode);
    }

    const widgetContainer = document.createElement('div');

    eventing(this);

    const onClick = () => this.trigger('user-interaction');
    widgetContainer.addEventListener('click', onClick);
    widgetContainer.addEventListener('touchstart', onClick, supportsPassive ? { passive: true } : false);
    let clearClickListener = () => {
      widgetContainer.removeEventListener('click', onClick);
      widgetContainer.removeEventListener('touchstart', onClick, supportsPassive ? { passive: true } : false);
    };

    if (properties && container) {
      width = properties.width;
      height = properties.height;

      if (width) {
        if (typeof width === 'number') {
          width += 'px';
        }
      }

      if (height) {
        if (typeof height === 'number') {
          height += 'px';
        }
      }

      container.style.width = width || '264px';
      container.style.height = height || '198px';
      container.style.overflow = 'hidden';
      fixMini(container, width || '264px', height || '198px');

      if (properties.mirror) {
        OTHelpers.addClass(container, 'OT_mirrored');
      }

      if (properties.fitMode === 'contain') {
        fitMode = 'contain';
      } else if (properties.fitMode !== 'cover') {
        logging.warn(`Invalid fit value "${properties.fitMode}" passed. ` +
        'Only "contain" and "cover" can be used.');
      }

      if (properties.classNames) { OTHelpers.addClass(container, properties.classNames); }

      OTHelpers(container).addClass(`OT_loading OT_fit-mode-${fitMode}`);
    }

    OTHelpers.addClass(widgetContainer, 'OT_widget-container');

    if (env.name === 'Safari' && isiOS() && iOSVersion() && versionGreaterThan('11.2', iOSVersion())) {
      OTHelpers(container).addClass('OT_ForceContain');
    }
    widgetContainer.style.width = '100%'; // container.style.width;
    widgetContainer.style.height = '100%'; // container.style.height;

    if (container) {
      container.appendChild(widgetContainer);
    }

    const posterContainer = document.createElement('div');
    OTHelpers.addClass(posterContainer, 'OT_video-poster');
    widgetContainer.appendChild(posterContainer);

    const loadingContainer = document.createElement('div');
    OTHelpers.addClass(loadingContainer, 'OT_video-loading');
    widgetContainer.appendChild(loadingContainer);

    const loadingSpinner = document.createElement('div');
    OTHelpers.addClass(loadingSpinner, 'OT_video-loading-spinner');
    loadingContainer.appendChild(loadingSpinner);

    if (container) {
      oldContainerStyles.width = container.offsetWidth;
      oldContainerStyles.height = container.offsetHeight;
    }

    if (!OTPlugin.isInstalled() && container) {
      // Observe changes to the width and height and update the aspect ratio
      sizeObserver = OTHelpers(container).observeSize(
        ({ width: w, height: h }) => {
          fixMini(container, w, h);
        })[0];

      // @todo observe if the video container or the video element get removed
      // if they do we should do some cleanup
      videoObserver = OTHelpers.observeNodeOrChildNodeRemoval(container, (removedNodes) => {
        if (!videoElementFacade) { return; }

        // This assumes a video element being removed is the main video element. This may
        // not be the case.
        const videoRemoved = removedNodes.some(node => node === widgetContainer || node.nodeName === 'VIDEO');

        if (videoRemoved) {
          this.destroyVideo();
        }
      });
    }

    /**
     * Destroy the video
     */
    this.destroy = function destroy() {
      if (clearClickListener) {
        clearClickListener();
        clearClickListener = null;
      }

      if (sizeObserver) {
        sizeObserver.disconnect();
        sizeObserver = null;
      }

      if (videoObserver) {
        videoObserver.disconnect();
        videoObserver = null;
      }

      this.destroyVideo();

      if (container) {
        OTHelpers.removeElement(container);
        container = null;
      }
    };

    this.setBackgroundImageURI = function setBackgroundImageURI(bgImgURI) {
      OTHelpers.css(posterContainer, 'backgroundImage', `url(${bgImgURI})`);
      OTHelpers.css(posterContainer, 'backgroundSize', 'contain');
      OTHelpers.css(posterContainer, 'opacity', '1.0');
    };

    if (properties && properties.style && properties.style.backgroundImageURI) {
      this.setBackgroundImageURI(properties.style.backgroundImageURI);
    }

    this.isAudioBlocked = () => videoElementFacade.isAudioBlocked();
    this.playAudio = () => videoElementFacade.playAudio();

    this.setAudioBlockedUi = (audioBlocked) => {
      if (audioBlocked) {
        OTHelpers.addClass(container, 'OT_container-audio-blocked');
      } else {
        OTHelpers.removeClass(container, 'OT_container-audio-blocked');
      }
    };

    /**
     * Bind a video to a VideoElementFacade
     *
     * @param {MediaStream} webRTCStream the MediaStream to bind
     * @param {Object} options
     * @param {Function} options.error Error callback
     * @param {Float} options.audioVolume The initial audioVolume
     * @param {Boolean} options.muted The initial mute state
     * @param {String} [options.fallbackText] The default fallbackText
     *
     * @return {VideoElementFacade}
     */
    this.bindVideo = function bindVideo(webRTCStream, {
      error: onError,
      audioVolume,
      muted,
      fallbackText,
      _inject,
    }, completion) {
      // remove the old video element if it exists
      // @todo this might not be safe, publishers/subscribers use this as well...

      this.destroyVideo();

      videoElementFacade = new WidgetView.VideoElementFacade({
        defaultAudioVolume: parseFloat(audioVolume, 100),
        fallbackText,
        fitMode,
        _inject,
        muted,
      }, onError);

      // Initialize the audio volume
      if (typeof audioVolume !== 'undefined') {
        try {
          videoElementFacade.setAudioVolume(audioVolume);
        } catch (e) {
          logging.warn(`bindVideo ${e}`);
        }
      }

      // makes the incoming audio streams take priority (will impact only FF OS for now)
      videoElementFacade.audioChannelType('telephony');

      videoElementFacade.appendTo(widgetContainer);

      videoElementFacade.bindToStream(webRTCStream, (err) => {
        if (err) {
          this.destroyVideo();
          completion(err);
          return;
        }

        videoElementFacade.on({
          videoDimensionsChanged: (oldValue, newValue) => {
            this.trigger('videoDimensionsChanged', oldValue, newValue);
          },
          mediaStopped: (track) => {
            this.trigger('mediaStopped', track);
          },
        });

        completion(null, videoElementFacade);
      });

      if (videoElementFacade.domElement()) {
        this.trigger('videoElementCreated', videoElementFacade.domElement());
      }

      videoElementFacade.on('videoElementCreated', (element) => {
        this.trigger('videoElementCreated', element);
      });

      if (properties.insertDefaultUI !== false) {
        OTHelpers.addClass(videoElementFacade.domElement(), 'OT_video-element');
      }

      return videoElementFacade;
    };

    this.destroyVideo = () => {
      if (videoElementFacade) {
        videoElementFacade.destroy();
        videoElementFacade = null;
      }
    };

    defineProperties(this, {
      video: {
        get() {
          return videoElementFacade;
        },
      },

      showPoster: {
        get() {
          return !OTHelpers.isDisplayNone(posterContainer);
        },
        set(newValue) {
          if (newValue) {
            OTHelpers.show(posterContainer);
          } else {
            OTHelpers.hide(posterContainer);
          }
        },
      },

      poster: {
        get() {
          return OTHelpers.css(posterContainer, 'backgroundImage');
        },
        set(src) {
          OTHelpers.css(posterContainer, 'backgroundImage', `url(${src})`);
        },
      },

      loading: {
        get() { return loading; },
        set(l) {
          loading = l;

          if (container) {
            if (loading) {
              OTHelpers.addClass(container, 'OT_loading');
            } else {
              OTHelpers.removeClass(container, 'OT_loading');
            }
          }
        },
      },

      audioOnly: {
        get() { return audioOnly; },
        set(_audioOnly) {
          audioOnly = _audioOnly;

          if (audioOnly) {
            OTHelpers.addClass(container, 'OT_audio-only');
          } else {
            OTHelpers.removeClass(container, 'OT_audio-only');
          }
        },
      },

      domId: {
        get() { return container && container.getAttribute('id'); },
      },

    });

    this.domElement = container;

    this.addError = function addError(errorMsg, helpMsg, classNames) {
      if (container) {
        container.innerHTML = `<p>${errorMsg
        }${helpMsg ? ` <span class="ot-help-message">${helpMsg}</span>` : ''
        }</p>`;
        OTHelpers.addClass(container, classNames || 'OT_subscriber_error');
        if (container.querySelector('p').offsetHeight > container.offsetHeight) {
          container.querySelector('span').style.display = 'none';
        }
      }
    };
  }

  // This is bound here so that it can be mocked in testing. Feels like a smell that's a symptom of
  // larger problems to me, but I'm just maintaining existing behaviour right now.
  WidgetView.VideoElementFacade = VideoElementFacade;

  return WidgetView;
}

module.exports = WidgetViewFactory;



// WEBPACK FOOTER //
// ./src/js/helpers/widget_view.js